import JourneyCardSkeleton from '@/components/purchase/journey/JourneyCardSkeleton';
import JourneyList from '@/components/purchase/journey/JourneyList';
import { outboundOrInboundJourneysLoadingSelector } from '@/features/loading/loadingSelectors';
import {
  getInboundJourneys,
  getOutboundJourneys,
  setSelectedOutboundJourney,
} from '@/features/purchase/purchaseActions';
import { outboundDataSelector } from '@/features/purchase/purchaseSelectors';
import { useSearchWidget } from '@/hooks/useSearchWidget';
import { TransText } from '@/i18n/trans/text';
import { useDispatch, useSelector } from '@/store/utils';
import { formatFullDate } from '@/utils/dateTime';
import { GTM_EVENT } from '@/utils/gtm';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const OutboundJourneyList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const outboundData = useSelector(outboundDataSelector);
  const loading = useSelector(outboundOrInboundJourneysLoadingSelector);
  const { currentSearchFormValues } = useSearchWidget();

  useEffect(() => {
    // If no journeys in Redux, check if we have search form values to fetch
    if (!outboundData.journeys && !loading) {
      if (
        currentSearchFormValues?.origin?.value &&
        currentSearchFormValues?.destination?.value
      ) {
        // Fetch outbound journeys
        const outboundPromise = dispatch(
          getOutboundJourneys({
            originStop: { code: currentSearchFormValues.origin.value },
            destinationStop: {
              code: currentSearchFormValues.destination.value,
            },
            departureTime: formatFullDate(
              currentSearchFormValues.departureDate
            ),
            passengers: currentSearchFormValues.passengers,
            promoCodes: currentSearchFormValues.promoCodes,
            ...(currentSearchFormValues.returnDate
              ? {
                  returnSearchParameters: {
                    inwardReturnDate: formatFullDate(
                      currentSearchFormValues.returnDate
                    ),
                  },
                }
              : {}),
          })
        );

        // Also fetch inbound journeys if return date exists
        if (currentSearchFormValues.returnDate) {
          dispatch(
            getInboundJourneys({
              originStop: {
                code: currentSearchFormValues.destination.value,
              },
              destinationStop: { code: currentSearchFormValues.origin.value },
              departureTime: formatFullDate(currentSearchFormValues.returnDate),
              passengers: currentSearchFormValues.passengers,
              promoCodes: currentSearchFormValues.promoCodes,
              returnSearchParameters: {
                outwardTripDate: formatFullDate(
                  currentSearchFormValues.departureDate
                ),
              },
            })
          );
        }
      } else {
        // No form values, redirect back to search
        navigate('/purchase');
      }
    }
  }, [
    outboundData.journeys,
    loading,
    currentSearchFormValues,
    dispatch,
    navigate,
  ]);

  // Show skeleton while loading and no journeys yet
  if (loading && !outboundData.journeys) {
    return (
      <div className="mx-auto w-full max-w-laptop p-4">
        <div className="mx-auto max-w-mobile">
          <div className="grid grid-cols-[1fr_auto] gap-y-4">
            {[1, 2, 3, 4].map((i) => (
              <JourneyCardSkeleton key={i} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <JourneyList
      {...outboundData}
      setSelectedJourney={setSelectedOutboundJourney}
      onContinue={() => navigate('/purchase/outbound/offer')}
      journeySelectedEvent={GTM_EVENT.OUTBOUND_JOURNEY_SELECTED}
      title={
        outboundData.journeys && outboundData.journeys.length > 0 ? (
          <TransText i18nKey="selectOutboundTrip" />
        ) : undefined
      }
    />
  );
};

export default OutboundJourneyList;
